import React, { useContext, useEffect, useState } from 'react';
import {
  Card,
  Button,
  Upload,
  Image,
  Row,
  Col,
  message,
  Space,
  Popconfirm,
  Empty,
  Spin,
  Radio,
  Modal
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import axios from 'axios';

const CCarruselSucursal = () => {
  const { user } = useContext(LoginContext);
  const [carrusel, setCarrusel] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [tipoDispositivo, setTipoDispositivo] = useState('desktop');
  const [fileToUpload, setFileToUpload] = useState(null);

  const sucursalId = user?.usu_sucursal;

  useEffect(() => {
    if (sucursalId) {
      fetchCarrusel();
    }
  }, [sucursalId]);

  const fetchCarrusel = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/carrusel/sucursal/${sucursalId}?soloActivos=false`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      setCarrusel(response.data);
    } catch (error) {
      console.error('Error al obtener carrusel:', error);
      message.error('Error al cargar el carrusel');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (file) => {
    if (!sucursalId) {
      message.error('No se pudo identificar la sucursal');
      return false;
    }

    // Validar que no haya más de 10 imágenes por tipo
    const imagenesDelTipo = carrusel.filter(c => c.car_tipo_dispositivo === tipoDispositivo);
    if (imagenesDelTipo.length >= 10) {
      message.warning(`Ya tienes el máximo de 10 imágenes para ${tipoDispositivo === 'desktop' ? 'escritorio' : 'móvil'}`);
      return false;
    }

    setFileToUpload(file);
    setUploadModalVisible(true);
    return false; // Evitar que antd suba el archivo automáticamente
  };

  const handleUpload = async () => {
    if (!fileToUpload) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('imagen', fileToUpload);
      
      // Contar imágenes del mismo tipo para el orden
      const imagenesDelTipo = carrusel.filter(c => c.car_tipo_dispositivo === tipoDispositivo);
      formData.append('car_orden', imagenesDelTipo.length);
      formData.append('car_activo', 'true');
      formData.append('car_tipo_dispositivo', tipoDispositivo);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/carrusel/sucursal/${sucursalId}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      message.success('Imagen subida correctamente');
      setUploadModalVisible(false);
      setFileToUpload(null);
      setTipoDispositivo('desktop');
      fetchCarrusel();
    } catch (error) {
      console.error('Error al subir imagen:', error);
      message.error('Error al subir la imagen');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (carId) => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/carrusel/${carId}`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      message.success('Imagen eliminada correctamente');
      fetchCarrusel();
    } catch (error) {
      console.error('Error al eliminar imagen:', error);
      message.error('Error al eliminar la imagen');
    }
  };

  const handleMoveOrder = async (carId, direction) => {
    // Solo reordenar dentro del mismo tipo (escritorio / móvil)
    const itemsDelTipo = carrusel
      .filter((c) => c.car_tipo_dispositivo === tipoDispositivo)
      .slice()
      .sort((a, b) => {
        const ordenA = Number(a.car_orden) || 0;
        const ordenB = Number(b.car_orden) || 0;
        if (ordenA !== ordenB) return ordenA - ordenB;
        return Number(a.car_id) - Number(b.car_id);
      });

    const currentIndex = itemsDelTipo.findIndex((c) => String(c.car_id) === String(carId));
    if (currentIndex < 0) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= itemsDelTipo.length) return;

    const reordered = [...itemsDelTipo];
    [reordered[currentIndex], reordered[newIndex]] = [reordered[newIndex], reordered[currentIndex]];

    // Reasignar órdenes consecutivos para evitar duplicados (ej. 4 y 4)
    const updates = reordered.map((item, idx) => ({
      ...item,
      car_orden: idx
    }));

    // Optimistic UI
    setCarrusel((prev) => {
      const otros = prev.filter((c) => c.car_tipo_dispositivo !== tipoDispositivo);
      return [...otros, ...updates].sort(
        (a, b) => (Number(a.car_orden) || 0) - (Number(b.car_orden) || 0)
      );
    });

    try {
      await Promise.all(
        updates.map((item) =>
          axios.put(
            `${process.env.REACT_APP_API_URL}/carrusel/${item.car_id}`,
            { car_orden: item.car_orden },
            { headers: { Authorization: `Bearer ${user.token}` } }
          )
        )
      );

      message.success('Orden actualizado');
      fetchCarrusel();
    } catch (error) {
      console.error('Error al cambiar orden:', error);
      message.error('Error al cambiar el orden');
      fetchCarrusel();
    }
  };

  const uploadProps = {
    beforeUpload: handleFileSelect,
    showUploadList: false,
    accept: 'image/*',
    maxCount: 1
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Card
        title="Gestión de Carrusel"
        extra={
          <Upload {...uploadProps} disabled={uploading || carrusel.length >= 10}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              loading={uploading}
              disabled={carrusel.length >= 10}
              style={{ backgroundColor: '#01634C', borderColor: '#01634C', color: '#fff' }}
            >
              Subir Imagen {carrusel.length >= 10 && '(Máximo 10)'}
            </Button>
          </Upload>
        }
      >
        {carrusel.length === 0 ? (
          <Empty description="No hay imágenes en el carrusel. Sube al menos una imagen para comenzar." />
        ) : (
          <>
            <div style={{ marginBottom: '16px' }}>
              <Radio.Group 
                value={tipoDispositivo} 
                onChange={(e) => setTipoDispositivo(e.target.value)}
                style={{ marginBottom: '16px' }}
              >
                <Radio.Button value="desktop">Escritorio</Radio.Button>
                <Radio.Button value="mobile">Móvil</Radio.Button>
              </Radio.Group>
            </div>
            <Row gutter={[16, 16]}>
              {carrusel
                .filter(item => item.car_tipo_dispositivo === tipoDispositivo)
                .slice()
                .sort((a, b) => {
                  const ordenA = Number(a.car_orden) || 0;
                  const ordenB = Number(b.car_orden) || 0;
                  if (ordenA !== ordenB) return ordenA - ordenB;
                  return Number(a.car_id) - Number(b.car_id);
                })
                .map((item, index, arr) => (
              <Col xs={24} sm={12} md={8} lg={6} key={item.car_id}>
                <Card
                  hoverable
                  cover={
                    <div style={{ position: 'relative', height: '200px', overflow: 'hidden', backgroundColor: '#f0f0f0' }}>
                      <img
                        src={item.car_imagen_url}
                        alt={`Carrusel ${index + 1}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x200?text=Error+al+cargar';
                        }}
                      />
                      {!item.car_activo && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            background: 'rgba(0,0,0,0.6)',
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: 4,
                            fontSize: 12
                          }}
                        >
                          Inactivo
                        </div>
                      )}
                    </div>
                  }
                  actions={[
                    <Button
                      type="text"
                      icon={<ArrowUpOutlined />}
                      onClick={() => handleMoveOrder(item.car_id, 'up')}
                      disabled={index === 0}
                      title="Mover arriba"
                    />,
                    <Button
                      type="text"
                      icon={<ArrowDownOutlined />}
                      onClick={() => handleMoveOrder(item.car_id, 'down')}
                      disabled={index === arr.length - 1}
                      title="Mover abajo"
                    />,
                    <Popconfirm
                      title="¿Eliminar esta imagen?"
                      onConfirm={() => handleDelete(item.car_id)}
                      okText="Sí"
                      cancelText="No"
                    >
                      <Button
                        type="text"
                        icon={<DeleteOutlined />}
                        title="Eliminar"
                        style={{ color: '#01634C' }}
                      />
                    </Popconfirm>
                  ]}
                >
                  <Card.Meta
                    title={`Orden: ${index + 1}`}
                    description={
                      <Space direction="vertical" size="small">
                        <span>Activo: {item.car_activo ? 'Sí' : 'No'}</span>
                        <span style={{ fontSize: '12px', color: '#888' }}>
                          {item.car_tipo_dispositivo === 'desktop' ? '🖥️ Escritorio' : '📱 Móvil'}
                        </span>
                      </Space>
                    }
                  />
                </Card>
              </Col>
                ))}
            </Row>
          </>
        )}
      </Card>

      <Modal
        title="Seleccionar tipo de dispositivo"
        open={uploadModalVisible}
        onOk={handleUpload}
        onCancel={() => {
          setUploadModalVisible(false);
          setFileToUpload(null);
        }}
        okText="Subir"
        cancelText="Cancelar"
        confirmLoading={uploading}
        okButtonProps={{ style: { backgroundColor: '#01634C', borderColor: '#01634C' } }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Tipo de dispositivo:
            </label>
            <Radio.Group 
              value={tipoDispositivo} 
              onChange={(e) => setTipoDispositivo(e.target.value)}
            >
              <Radio value="desktop">Escritorio</Radio>
              <Radio value="mobile">Móvil</Radio>
            </Radio.Group>
          </div>
          <div style={{ color: '#666', fontSize: '12px' }}>
            {tipoDispositivo === 'desktop' 
              ? 'Esta imagen se mostrará en dispositivos de escritorio (computadoras, tablets grandes)'
              : 'Esta imagen se mostrará en dispositivos móviles (celulares, tablets pequeñas)'}
          </div>
        </Space>
      </Modal>
    </div>
  );
};

export default CCarruselSucursal;
